import { TabType, type TabState } from 'owa-tab-store';
import { getStore as getTabStore } from 'owa-tab-store/lib/store/tabStore';
import type { ComposeViewState } from 'owa-mail-compose-store';
import { type AsyncSendState, getStore as getComposeStore } from 'owa-mail-compose-store';
import { assertNever } from 'owa-assert';
import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';

/**
 * Finds the ComposeViewState from a id tab and returns it.
 * @param {string} id that contains either a ConversationId or a ReferenceItemId property that is part of the ComposeViewState.
 * @param {ReactListViewType} idType that represents either a ConversationView or ItemView to loopk up for the.
 * By default we look for ConversationView ComposeViewStates.
 * @param {boolean} includeDelayedSendState optional parameter to include delayed send states or not.
 * @returns ComposeViewState containing the compose view for the given tab.
 */
export default function findInlineComposeViewState(
    id: string,
    idType: ReactListViewType = 0, // To keep the existing functionality, by default, the inline compose is a conversation.
    includeDelayedSendState?: boolean,
    projectionRPTabId?: string
): ComposeViewState | undefined {
    const composeContainsId = getListViewChecker(idType);
    const composeStore = getComposeStore();

    for (const tab of getTabStore().tabs) {
        if (tab && tab.type == TabType.MailCompose && tab.state == 0) {
            const viewState = composeStore.viewStates.get(tab.data);
            if (
                viewState?.isInlineCompose &&
                composeContainsId(viewState, id) &&
                (viewState.asyncSendState == 0 ||
                    (includeDelayedSendState && viewState.asyncSendState == 6)) &&
                ((!projectionRPTabId && !tab.projectionRPTabId) ||
                    tab.projectionRPTabId == projectionRPTabId)
            ) {
                return viewState;
            }
        }
    }

    return undefined;
}

function getListViewChecker(
    reactListViewType: ReactListViewType
): (viewState: ComposeViewState, id: string) => boolean {
    switch (reactListViewType) {
        case 0:
            return conversationIdChecker;
        case 1:
            return referenceItemIdChecker;
        case 2:
            // Need to include all the enums in the callback even if it is not supported.
            // calendarIdChecker function returns always false.
            return calendarIdChecker;
        default:
            return assertNever(reactListViewType);
    }
}

function conversationIdChecker(viewState: ComposeViewState, id: string): boolean {
    return viewState.conversationId === id;
}

function referenceItemIdChecker(viewState: ComposeViewState, id: string): boolean {
    return viewState.referenceItemId?.Id === id;
}

function calendarIdChecker(_viewState: ComposeViewState, _id: string): boolean {
    return false;
}
